import { type Locale, type LocationRow, adminApi } from '@/lib/api';
import { useMutation } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { MapPicker } from './MapPicker';

const LOCALES: Locale[] = ['pt', 'en', 'fr', 'es', 'de'];

interface Props {
  location: LocationRow | null;
  onClose: () => void;
  onSaved: () => void;
}

function emptyTr(): Record<Locale, string> {
  return { pt: '', en: '', fr: '', es: '', de: '' };
}

/**
 * Modal location editor — handles both create and edit. The 5-locale name + address tabs
 * keep the surface compact while still enforcing constitution §5: the API publish-gate
 * refuses to flip `active=true` until every locale has content, so we surface the gap
 * inline (X/5 completeness) instead of hiding it inside an error toast.
 */
export function LocationForm({ location, onClose, onSaved }: Props) {
  const isNew = location === null;
  const [activeLocale, setActiveLocale] = useState<Locale>('pt');
  const [name, setName] = useState<Record<Locale, string>>(location?.name ?? emptyTr());
  const [address, setAddress] = useState<Record<Locale, string>>(location?.address ?? emptyTr());
  const [lat, setLat] = useState<number | null>(location?.lat ? Number(location.lat) : null);
  const [lng, setLng] = useState<number | null>(location?.lng ? Number(location.lng) : null);
  const handlePick = useCallback((nextLat: number, nextLng: number) => {
    setLat(nextLat);
    setLng(nextLng);
  }, []);
  const [eurFee, setEurFee] = useState<number>(location?.eurFee ?? 0);
  const [cveFee, setCveFee] = useState<number>(location?.cveFee ?? 0);
  const [active, setActive] = useState<boolean>(location?.active ?? false);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        name,
        address,
        lat,
        lng,
        eurFee,
        cveFee,
        active,
      };
      return isNew ? adminApi.createLocation(body) : adminApi.patchLocation(location!.id, body);
    },
    onSuccess: onSaved,
    onError: (err) => {
      const msg = (err as Error).message;
      setError(
        msg.includes('incomplete_translations')
          ? 'Faltam traduções: para activar o local todas as 5 línguas têm de estar preenchidas (Nome + Morada).'
          : msg,
      );
    },
  });

  const nameDone = LOCALES.filter((l) => name[l]?.trim().length > 0).length;
  const addrDone = LOCALES.filter((l) => address[l]?.trim().length > 0).length;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(12, 35, 34, 0.55)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
      }}
      onClick={onClose}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          save.mutate();
        }}
        style={{
          background: '#fff',
          width: 560,
          padding: 28,
          border: '1px solid var(--lv-line)',
          maxHeight: '90vh',
          overflow: 'auto',
        }}
      >
        <div className="display" style={{ fontSize: 22 }}>
          {isNew ? 'NOVO LOCAL' : 'EDITAR LOCAL'}
        </div>
        <div className="label" style={{ color: 'var(--lv-brand)', marginTop: 4 }}>
          Levantamento e devolução
        </div>

        {/* locale tabs */}
        <div style={{ display: 'flex', gap: 4, marginTop: 20, borderBottom: '1px solid var(--lv-line)' }}>
          {LOCALES.map((l) => (
            <button
              key={l}
              type="button"
              onClick={() => setActiveLocale(l)}
              style={{
                background: activeLocale === l ? 'var(--lv-brand)' : 'transparent',
                color: activeLocale === l ? '#fff' : '#222',
                border: 0,
                padding: '8px 14px',
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                cursor: 'pointer',
              }}
            >
              {l}
            </button>
          ))}
          <div style={{ marginLeft: 'auto', alignSelf: 'center', fontSize: 11, color: 'var(--lv-gray-500)' }}>
            Nome {nameDone}/5 · Morada {addrDone}/5
          </div>
        </div>

        <div style={{ marginTop: 16, display: 'grid', gap: 12 }}>
          <Field label={`Nome (${activeLocale.toUpperCase()})`}>
            <input
              type="text"
              value={name[activeLocale]}
              onChange={(e) => setName({ ...name, [activeLocale]: e.target.value })}
              style={input}
            />
          </Field>
          <Field label={`Morada (${activeLocale.toUpperCase()})`}>
            <input
              type="text"
              value={address[activeLocale]}
              onChange={(e) => setAddress({ ...address, [activeLocale]: e.target.value })}
              style={input}
            />
          </Field>
        </div>

        <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--lv-line)' }}>
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'var(--lv-gray-500)',
              marginBottom: 8,
            }}
          >
            Localização no mapa
          </div>
          <MapPicker lat={lat} lng={lng} onPick={handlePick} />
        </div>

        <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Taxa EUR">
            <input
              type="number"
              step="0.5"
              value={eurFee}
              onChange={(e) => setEurFee(Number(e.target.value))}
              style={input}
            />
          </Field>
          <Field label="Taxa CVE">
            <input
              type="number"
              step="50"
              value={cveFee}
              onChange={(e) => setCveFee(Number(e.target.value))}
              style={input}
            />
          </Field>
        </div>

        <label style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          Activo (visível no site público)
        </label>
        {active && (nameDone < 5 || addrDone < 5) && (
          <div style={{ marginTop: 8, fontSize: 11, color: 'var(--lv-warn)' }}>
            Para activar, preencha todas as 5 línguas para Nome e Morada (gate de publicação).
          </div>
        )}

        {error && (
          <div
            style={{
              marginTop: 16,
              padding: '8px 12px',
              background: '#fce4e4',
              color: 'var(--lv-err)',
              fontSize: 12,
            }}
          >
            {error}
          </div>
        )}

        <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" onClick={onClose} style={btnGhost}>
            Cancelar
          </button>
          <button type="submit" disabled={save.isPending} style={btnPrimary}>
            {save.isPending ? '…' : 'Guardar'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'block' }}>
      <span
        style={{
          display: 'block',
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'var(--lv-gray-500)',
        }}
      >
        {label}
      </span>
      <span style={{ marginTop: 4, display: 'block' }}>{children}</span>
    </label>
  );
}

const input: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid var(--lv-line)',
  background: '#fff',
  fontSize: 13,
};
const btnPrimary: React.CSSProperties = {
  background: 'var(--lv-brand)',
  color: '#fff',
  border: 0,
  padding: '10px 18px',
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  cursor: 'pointer',
};
const btnGhost: React.CSSProperties = {
  background: '#fff',
  color: '#222',
  border: '1px solid var(--lv-line)',
  padding: '10px 18px',
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  cursor: 'pointer',
};
